import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Button, Paper, CircularProgress, Alert, Box, Divider } from '@mui/material';
import { getDoc, doc, updateDoc, arrayUnion, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase.config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { getDeckById, addUserDeck } from '../services/deckService';

const JoinClassPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [classData, setClassData] = useState<any>(null);
  const [teacherName, setTeacherName] = useState<string>('');

  const fetchClassData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!classId) {
        setError(t('classes.idNotFound'));
        setLoading(false);
        return;
      }
      
      console.log('Getting class data:', classId);
      const classDocRef = doc(db, 'classes', classId);
      const classDoc = await getDoc(classDocRef);
      
      if (!classDoc.exists()) {
        setError(t('classes.notFound'));
        setLoading(false);
        return;
      }
      
      const data = classDoc.data();
      console.log('Class data retrieved from Firestore:', data);
      setClassData(data);
      
      // Getting teacher information
      try {
        if (data.teacherId) {
          // Check if teacher information exists in the class document
          if (data.teacherName && data.teacherName.trim() !== '') {
            console.log('Using teacherName from class document:', data.teacherName);
            setTeacherName(data.teacherName);
            return;
          }
          
          // If no information in the class document, get from users collection
          console.log('Getting teacher information from users collection');
          const teacherDocRef = doc(db, 'users', data.teacherId);
          const teacherDoc = await getDoc(teacherDocRef);
          
          if (teacherDoc.exists()) {
            const teacherData = teacherDoc.data();
            console.log('Teacher data from users collection:', teacherData);
            
            if (teacherData.displayName && teacherData.displayName.trim() !== '') {
              console.log('Using displayName of the teacher:', teacherData.displayName);
              setTeacherName(teacherData.displayName);
            } else if (teacherData.email) {
              console.log('Using email of the teacher:', teacherData.email);
              setTeacherName(teacherData.email);
            } else {
              setTeacherName(t('classes.unknownTeacher'));
            }
          } else {
            console.log('Teacher document not found');
            setTeacherName(t('classes.unknownTeacher'));
          }
        } else {
          console.log('Teacher ID is missing in the class document');
          setTeacherName(t('classes.unknownTeacher'));
        }
      } catch (err) {
        console.error('Error retrieving teacher data:', err);
        setTeacherName(t('classes.unknownTeacher'));
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error retrieving class data:', err);
      setError(t('classes.loadingError'));
      setLoading(false);
    }
  }, [classId, t]);

  useEffect(() => {
    if (classId) {
      if (auth.currentUser) {
        fetchClassData();
      } else {
        setError(t('classes.loginRequired'));
        setLoading(false);
      }
    }
  }, [classId, t, fetchClassData]);

  const handleJoinClass = async () => {
    if (!auth.currentUser || !classId) return;
    
    setLoading(true);
    try {
      console.log("[JoinClassPage] Joining class:", classId);
      
      // Adding the class to the user's class list
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, {
        classes: arrayUnion(classId)
      });
      
      // Adding the user to the class's student list
      const classDocRef = doc(db, 'classes', classId);
      const classSnapshot = await getDoc(classDocRef);
      if (!classSnapshot.exists()) {
        throw new Error(t('classes.notFound'));
      }
      
      // Get class data
      const classData = classSnapshot.data();
      console.log("[JoinClassPage] Class data:", classData);
      
      // Update class document, adding user to students array
      await updateDoc(classDocRef, {
        students: arrayUnion(auth.currentUser.uid)
      });
      
      // Add class decks to user's deck list
      if (classData.deckIds && classData.deckIds.length > 0) {
        console.log("[JoinClassPage] Class decks:", classData.deckIds);
        for (const deckId of classData.deckIds) {
          try {
            console.log("[JoinClassPage] Getting deck info:", deckId);
            // Get deck by ID
            const deck = await getDeckById(deckId);
            if (deck) {
              console.log("[JoinClassPage] Deck found:", deck.title);
              // Add note that deck is from class
              const deckWithClassInfo = {
                ...deck,
                fromClassId: classId,
                fromClassName: classData.name
              };
              
              // Add deck to user
              console.log("[JoinClassPage] Adding deck to user");
              await addUserDeck(auth.currentUser.uid, deckWithClassInfo);
            } else {
              console.log("[JoinClassPage] Deck not found:", deckId);
            }
          } catch (deckError) {
            console.error(`[JoinClassPage] Error adding deck ${deckId} to user:`, deckError);
          }
        }
      } else {
        console.log("[JoinClassPage] No decks in class");
      }
      
      // Add class to userClasses collection
      try {
        const userClassesRef = doc(db, 'userClasses', auth.currentUser.uid);
        const userClassesSnap = await getDoc(userClassesRef);
        
        if (userClassesSnap.exists()) {
          await updateDoc(userClassesRef, {
            classesJoined: arrayUnion(classId)
          });
        } else {
          await setDoc(userClassesRef, {
            classesJoined: [classId]
          });
        }
      } catch (userClassError) {
        console.error('[JoinClassPage] Error updating userClasses:', userClassError);
        // Continue execution, as the main action (adding to class) has already happened
      }
      
      console.log("[JoinClassPage] Successfully joined class");
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      console.error('[JoinClassPage] Error joining class:', err);
      setError(t('classes.joinError'));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('classes.loadingClass')}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/')}
        >
          {t('common.backToHome')}
        </Button>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('classes.joinSuccess')}
        </Alert>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => navigate(`/classes/${classId}`)}
          sx={{ mr: 2 }}
        >
          {t('classes.goToClass')}
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
        >
          {t('common.backToHome')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('classes.joinClass')}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        {classData && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {classData.name}
            </Typography>
            
            {classData.description && (
              <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
                {classData.description}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {t('classes.teacher')}: {teacherName}
              </Typography>
            </Box>
            
            {classData.deckIds && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LibraryBooksIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('classes.availableDecks')}: {classData.deckIds.length}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/')}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleJoinClass}
          >
            {t('classes.joinNow')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default JoinClassPage; 